import { useState, useCallback } from 'react';
import { trackerAPI } from '../api/tracker.api';
import type { NotificationSettings } from '../types/tracker.types';

export const useChannelSubscription = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subscribe = useCallback(async (channelId: string) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await trackerAPI.subscribeToChannel(channelId);
      return response.success;
    } catch (err) {
      console.error('Error subscribing to channel:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const unsubscribe = useCallback(async (channelId: string, force = false) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await trackerAPI.unsubscribeFromChannel(channelId, force);

      // If requires confirmation, return special status
      if (!response.success && response.requires_confirmation) {
        return {
          success: false,
          requiresConfirmation: true,
          message: response.message || 'This is an auto-subscription. Are you sure you want to unsubscribe?'
        };
      }

      return { success: response.success, requiresConfirmation: false };
    } catch (err: any) {
      console.error('Error unsubscribing from channel:', err);

      // Check if error response has requires_confirmation
      if (err?.response?.data?.requires_confirmation) {
        return {
          success: false,
          requiresConfirmation: true,
          message: err.response.data.message || 'This is an auto-subscription. Are you sure you want to unsubscribe?'
        };
      }

      setError(err instanceof Error ? err.message : 'Unknown error');
      return { success: false, requiresConfirmation: false };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const updateNotifications = useCallback(async (
    channelId: string,
    settings: {
      notifications_enabled?: boolean;
      notification_settings?: NotificationSettings;
    }
  ) => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await trackerAPI.updateNotificationSettings(channelId, settings);
      return response.success;
    } catch (err) {
      console.error('Error updating notification settings:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    subscribe,
    unsubscribe,
    updateNotifications,
    isLoading,
    error
  };
};
